import React, { ReactNode } from "react";

export interface TruncatedTooltipProps {
  /** The content to display and truncate */
  content: string | ReactNode;
  /** Additional CSS classes for the content wrapper */
  contentClassName?: string;
  /** Maximum width for truncation - defaults to max-w-[150px] */
  maxWidth?: string;
  /** Whether to use the table-row-main class */
  useTableRowMain?: boolean;
  /** Whether to use emphasized styling (font-medium text-gray-900) */
  emphasized?: boolean;
  /** Custom text styling classes */
  textClassName?: string;
  /** Tooltip content - defaults to content if not provided */
  tooltipContent?: string;
  /** Additional classes for the tooltip */
  tooltipClassName?: string;
  /** Whether content should be displayed as block */
  block?: boolean;
  /** Custom wrapper element - defaults to 'div' */
  as?: React.ElementType;
}

const TruncatedTooltip: React.FC<TruncatedTooltipProps> = ({
  content,
  contentClassName = "",
  maxWidth = "max-w-[150px]",
  useTableRowMain = false,
  emphasized = false,
  textClassName = "",
  tooltipContent,
  tooltipClassName = "",
  block = false,
  as: Component = "div",
}) => {
  // Build content classes based on props
  const getContentClasses = () => {
    const classes = ["truncate", maxWidth];

    if (useTableRowMain) {
      classes.push("table-row-main");
    }

    if (emphasized) {
      classes.push("font-medium", "text-gray-900");
    }

    if (block) {
      classes.push("block");
    }

    if (textClassName) {
      classes.push(textClassName);
    }

    if (contentClassName) {
      classes.push(contentClassName);
    }

    return classes.join(" ");
  };

  // Default tooltip classes
  const defaultTooltipClasses = [
    "absolute",
    "left-1/2",
    "-translate-x-1/2",
    "bottom-full",
    "mb-2",
    "px-2",
    "py-1",
    "bg-gray-900",
    "text-white",
    "text-sm",
    "rounded",
    "opacity-0",
    "pointer-events-none",
    "group-hover:opacity-100",
    "transition-opacity",
    "duration-200",
    "whitespace-nowrap",
    "z-50",
    "min-w-max",
  ];

  const tooltipClasses = tooltipClassName
    ? `${defaultTooltipClasses.join(" ")} ${tooltipClassName}`
    : defaultTooltipClasses.join(" ");

  const displayContent = typeof content === "string" ? content : content;
  const displayTooltip =
    tooltipContent || (typeof content === "string" ? content : "");

  return (
    <Component className="relative group">
      <span className={getContentClasses()}>{displayContent}</span>
      <span className={tooltipClasses}>{displayTooltip}</span>
    </Component>
  );
};

export default TruncatedTooltip;
